import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink, PiggyBank } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { fmtDate, priceUsd } from '@/lib/billingApi';
import { AnnualSwitchDialog } from './AnnualSwitchDialog';

const PaypalSteps = ({ sub }) => (
  <ol className="mt-4 space-y-2 text-sm text-slate-700 list-decimal list-inside" data-testid="annual-offer-paypal-steps">
    <li>
      Cancel your monthly plan in PayPal. You keep Pro until <b>{fmtDate(sub.current_period_end)}</b>.{' '}
      <a href={sub.manage_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-[#00247D] underline underline-offset-2" data-testid="annual-offer-paypal-link">
        Manage in PayPal<ExternalLink className="w-3.5 h-3.5" />
      </a>
    </li>
    <li>
      Once it ends, subscribe to Pro Annual on the{' '}
      <Link to="/pricing" className="font-semibold text-[#00247D] underline underline-offset-2" data-testid="annual-offer-pricing-link">Pricing page</Link>.
    </li>
  </ol>
);

const CardSwitch = ({ sub, offer, cardEnabled }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
      <Button onClick={() => setOpen(true)} disabled={!cardEnabled} className="bg-amber-400 hover:bg-amber-300 text-[#00247D] font-black transition-transform active:scale-[0.98]" data-testid="annual-offer-switch-btn">
        Switch to annual<ArrowRight className="w-4 h-4 ml-1.5" />
      </Button>
      <p className="text-xs text-slate-500">
        {cardEnabled ? `Pay ${priceUsd(offer.annual_price)} now. Your year starts ${fmtDate(sub.current_period_end)}, when your paid month ends.`
          : 'Card payments are temporarily unavailable. Please try again later.'}
      </p>
      {open && <AnnualSwitchDialog sub={sub} offer={offer} onClose={() => setOpen(false)} />}
    </div>
  );
};

// Savings nudge for Pro Monthly subscribers (card: one-click switch; PayPal: steps).
export const AnnualSwitchOffer = ({ sub, cardEnabled }) => {
  const offer = sub.annual_switch;
  return (
    <div className="rounded-2xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-white p-5 sm:p-6 animate-in fade-in slide-in-from-bottom-2 duration-500" data-testid="annual-offer">
      <div className="flex items-start gap-3">
        <PiggyBank className="w-7 h-7 text-amber-600 shrink-0" />
        <div className="min-w-0">
          <h3 className="text-2xl sm:text-3xl font-black uppercase text-slate-900 leading-none" style={{ fontFamily: 'Barlow Condensed' }} data-testid="annual-offer-title">
            Save {priceUsd(offer.savings)}/year with Pro Annual
          </h3>
          <p className="text-sm text-slate-600 mt-2" data-testid="annual-offer-math">
            Monthly costs {priceUsd(offer.yearly_at_monthly)} a year ({priceUsd(offer.monthly_amount)} × 12). Annual is {priceUsd(offer.annual_price)} a year, with the same Pro features.
          </p>
          {offer.drops_discount && <p className="text-xs text-amber-800 mt-1.5" data-testid="annual-offer-discount-note">Your monthly discount doesn't carry over to Pro Annual.</p>}
        </div>
      </div>
      {sub.source === 'paypal' ? <PaypalSteps sub={sub} /> : <CardSwitch sub={sub} offer={offer} cardEnabled={cardEnabled} />}
    </div>
  );
};
